import type { AlertsCenterPresentation } from '../utils/alert/alertsCenterCard';

/** The two composer-height presentations the full center can return to. */
export type AlertsComposerPresentation = Exclude<AlertsCenterPresentation, 'full'>;

export interface IAlertsPresentationState {
  presentation: AlertsCenterPresentation;
  /** The composer presentation the full center was opened from — where closing it lands. */
  returnTo: AlertsComposerPresentation;
  /**
   * Whether the session's one send-driven collapse is spent. The first send
   * moves the truncated card to the pill and later sends leave it alone; an
   * alert expansion spends it too, though it collapses the card every time.
   */
  hasAutoCollapsed: boolean;
}

/**
 * What can move the card:
 * - `expand` — the pill was clicked.
 * - `collapse` — the chevron on the truncated card or the full center: down to the pill.
 * - `viewAll` — the truncated card's "View all".
 * - `open` — the bell, or an `/alerts` deep link; a no-op while already full so
 *   a deep link keeps the origin a bell press recorded.
 * - `close` — Escape, or the bell pressed again: back to where the full center was opened from.
 * - `alertExpanded` — an alert entered the thread; the card lands on the pill, every time.
 * - `userSent` — the user sent a message.
 * - `rowFixRan` — a row with a fix of its own (a reconnect, a settings hop) ran it.
 * - `showTruncated` — the page's floating assistant pill, with alerts to review:
 *   the truncated card from any presentation, where `expand` moves only the pill.
 */
export type AlertsPresentationAction =
  | 'expand'
  | 'collapse'
  | 'viewAll'
  | 'open'
  | 'close'
  | 'alertExpanded'
  | 'userSent'
  | 'rowFixRan'
  | 'showTruncated';

/** A session starts on the truncated card. In-memory; resets on reload. */
export const INITIAL_ALERTS_PRESENTATION: IAlertsPresentationState = {
  presentation: 'truncated',
  returnTo: 'truncated',
  hasAutoCollapsed: false,
};

/**
 * Collapses the truncated card on the session's first send, then leaves sends
 * to the user: the same object comes back when nothing changes, so React skips
 * the render.
 */
const autoCollapseOnce = (state: IAlertsPresentationState): IAlertsPresentationState => {
  if (state.hasAutoCollapsed) {
    return state;
  }

  return {
    ...state,
    presentation: state.presentation === 'truncated' ? 'collapsed' : state.presentation,
    hasAutoCollapsed: true,
  };
};

/**
 * The Notification Center's presentation machine. Pure, so every transition in
 * the click table is a unit test; `SidePanelProvider` holds the state and
 * exposes one callback per action.
 *
 * Returns the same state object on a no-op so a dispatch that changes nothing
 * costs no render.
 */
export function reduceAlertsPresentation(
  state: IAlertsPresentationState,
  action: AlertsPresentationAction
): IAlertsPresentationState {
  const { presentation } = state;

  switch (action) {
    case 'expand':
      return presentation === 'collapsed' ? { ...state, presentation: 'truncated' } : state;

    case 'collapse':
      return presentation === 'collapsed' ? state : { ...state, presentation: 'collapsed' };

    case 'viewAll':
      return presentation === 'truncated' ? { ...state, presentation: 'full', returnTo: 'truncated' } : state;

    case 'open':
      return presentation === 'full' ? state : { ...state, presentation: 'full', returnTo: presentation };

    case 'close':
      return presentation === 'full' ? { ...state, presentation: state.returnTo } : state;

    case 'alertExpanded':
      // Opening a row is the user's own move, so the card steps aside for the
      // reply whatever the send-driven collapse has done — and spends it.
      if (presentation === 'collapsed' && state.hasAutoCollapsed) {
        return state;
      }
      return { ...state, presentation: 'collapsed', hasAutoCollapsed: true };

    case 'userSent':
      return autoCollapseOnce(state);

    case 'rowFixRan':
      return presentation === 'full' ? { ...state, presentation: 'collapsed' } : state;

    case 'showTruncated':
      // The user's own ask, like the bell's `open`, so the send-driven collapse is left as it stands.
      return presentation === 'truncated' ? state : { ...state, presentation: 'truncated' };

    // no default
  }
}
